import { ETA_COPY } from "@the-drop/customer-shared/eta-copy";

/**
 * Three-state ETA card body for the customer order-tracking screen. Pure render — no clock, no
 * `now()` reads — so the same input always renders the same output and tests can assert each
 * state from a fixed `etaWindow` prop.
 *
 * Payload contract obeys the customer-disclosure rule from
 * `feedback_customer_driver_disclosure.md` — no coordinates, no stops-ahead, no "where" or
 * "when" specifics about other deliveries. Generic copy only.
 *
 * **Shared copy:** all rendered strings come from `@the-drop/customer-shared/eta-copy` so the
 * web and mobile (`apps/customer-mobile/src/components/EtaCardBody.tsx`) surfaces stay in sync.
 * The disclosure-rule regex tests below cover both surfaces transitively — a future copy edit
 * that introduces a count / address / relative-timing leak will fail this gate before it can
 * ship to either app.
 *
 * Whole-minute precision: the orchestrator already truncates `etaStart`/`etaEnd` to minute
 * boundaries, so the display formatter just renders what's in the ISO string without sub-minute
 * jitter.
 */

export type EtaCardWindow = {
  etaStart: string;
  etaEnd: string;
  computedAt: string;
  runningLate: boolean;
};

export function EtaCardBody({ etaWindow }: { etaWindow: EtaCardWindow | null }) {
  if (!etaWindow) {
    return (
      <div className="px-5 py-8 text-center" data-eta-state="pre-run">
        <p className="text-sm font-semibold text-[var(--text-primary)]">{ETA_COPY.preRun.heading}</p>
        <p className="mt-1 text-xs text-[var(--text-muted)]">{ETA_COPY.preRun.body}</p>
      </div>
    );
  }

  const startLabel = formatEtaWindowEdge(etaWindow.etaStart);
  const endLabel = formatEtaWindowEdge(etaWindow.etaEnd);

  if (etaWindow.runningLate) {
    return (
      <div className="px-5 py-8 text-center" data-eta-state="running-late">
        <p className="text-sm font-semibold text-amber-900">{ETA_COPY.runningLate.heading}</p>
        <p className="mt-1 text-xs text-[var(--text-muted)]">{ETA_COPY.runningLate.body}</p>
        <p className="mt-2 text-base font-semibold text-[var(--text-primary)]">
          {ETA_COPY.runningLate.estimatePrefix} {startLabel}&nbsp;–&nbsp;{endLabel}
        </p>
      </div>
    );
  }

  return (
    <div className="px-5 py-8 text-center" data-eta-state="window">
      <p className="text-xs uppercase tracking-wide text-[var(--text-muted)]">
        {ETA_COPY.window.eyebrow}
      </p>
      <p className="mt-1 text-2xl font-bold text-[var(--text-primary)]">
        {startLabel}&nbsp;–&nbsp;{endLabel}
      </p>
      <p className="mt-1 text-xs text-[var(--text-muted)]">{ETA_COPY.window.body}</p>
    </div>
  );
}

/**
 * h:mm A formatter (e.g. "4:25 PM"). Locale-stable so tests don't depend on the host's `Intl`
 * settings. Whole-minute precision per `docs/driver-eta.md`.
 */
export function formatEtaWindowEdge(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "—";
  let hours = d.getHours();
  const minutes = d.getMinutes();
  const meridiem = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  if (hours === 0) hours = 12;
  const mm = String(minutes).padStart(2, "0");
  return `${hours}:${mm} ${meridiem}`;
}
